import { useCallback, useEffect, useState } from 'react'
import { Percent, Plus, UserRound, X } from 'lucide-react'

import { CommissionProfilesPanel } from '@/components/masters/CommissionProfilesPanel'
import { MasterFormModal } from '@/components/masters/MasterFormModal'
import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import { fetchCommissionProfiles } from '@/lib/api/commissionProfiles'
import {
  COMMON_STYLIST_SKILLS,
  createStylist,
  fetchStylistsList,
  fetchStylistsPage,
  normalizeSkill,
  updateStylist,
} from '@/lib/api/stylists'
import { cn } from '@/lib/utils'

function emptyForm() {
  return {
    name: '',
    skills: [],
    colour: '#D4A373',
    bookable_online: true,
    wps_employee_id: '',
    bank_iban: '',
    commission_profile_id: '',
    is_active: true,
  }
}

/**
 * @param {import('@/types/api').Stylist} stylist
 */
function stylistToForm(stylist) {
  return {
    name: stylist.name ?? '',
    skills: Array.isArray(stylist.skills) ? stylist.skills : [],
    colour: stylist.colour ?? '#D4A373',
    bookable_online: Boolean(stylist.bookable_online),
    wps_employee_id: stylist.wps_employee_id ?? '',
    bank_iban: stylist.bank_iban ?? '',
    commission_profile_id: stylist.commission_profile_id ?? '',
    is_active: Boolean(stylist.is_active),
  }
}

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

function formatSkillLabel(skill) {
  return skill.replace(/_/g, ' ')
}

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  {
    key: 'skills',
    label: 'Skills',
    getValue: (row) => (row.skills ?? []).join(', '),
  },
  { key: 'colour', label: 'Colour', getValue: (row) => row.colour ?? '' },
  {
    key: 'wps_employee_id',
    label: 'WPS employee ID',
    getValue: (row) => row.wps_employee_id ?? '',
  },
  { key: 'bank_iban', label: 'Bank IBAN', getValue: (row) => row.bank_iban ?? '' },
  {
    key: 'commission_profile',
    label: 'Commission profile',
    getValue: (row) => row.commission_profile?.name ?? '',
  },
  {
    key: 'bookable_online',
    label: 'Bookable online',
    getValue: (row) => (row.bookable_online ? 'yes' : 'no'),
  },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

export function StylistsPage() {
  const { activeBranchId } = useBranch()
  const [activeTab, setActiveTab] = useState('staff')
  const [commissionProfiles, setCommissionProfiles] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState(emptyForm())
  const [skillInput, setSkillInput] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  const fetchPage = useCallback((params) => fetchStylistsPage(params), [])

  useEffect(() => {
    let active = true

    async function loadCommissionProfiles() {
      try {
        const items = await fetchCommissionProfiles()
        if (!active) return
        setCommissionProfiles(items.filter((item) => item.is_active))
      } catch {
        if (!active) return
        setCommissionProfiles([])
      }
    }

    loadCommissionProfiles()
    return () => {
      active = false
    }
  }, [activeBranchId])

  function openCreate() {
    setCreating(true)
    setSelectedId(null)
    setForm(emptyForm())
    setSkillInput('')
    setError('')
    setModalOpen(true)
  }

  /**
   * @param {import('@/types/api').Stylist} stylist
   */
  function openEdit(stylist) {
    setCreating(false)
    setSelectedId(stylist.id)
    setForm(stylistToForm(stylist))
    setSkillInput('')
    setError('')
    setModalOpen(true)
  }

  function closeModal() {
    if (submitting) return
    setModalOpen(false)
    setError('')
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  function addSkill(rawSkill) {
    const skill = normalizeSkill(rawSkill)
    if (!skill) return

    setForm((current) => {
      if (current.skills.includes(skill)) return current
      return { ...current, skills: [...current.skills, skill] }
    })
    setSkillInput('')
  }

  function removeSkill(skill) {
    setForm((current) => ({
      ...current,
      skills: current.skills.filter((item) => item !== skill),
    }))
  }

  function handleSkillKeyDown(event) {
    if (event.key !== 'Enter') return
    event.preventDefault()
    addSkill(skillInput)
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')

    const payload = {
      ...form,
      colour: form.colour || null,
      wps_employee_id: form.wps_employee_id || null,
      bank_iban: form.bank_iban || null,
      commission_profile_id:
        form.commission_profile_id === '' ? null : Number(form.commission_profile_id),
    }

    try {
      if (creating) {
        await createStylist(payload)
      } else if (selectedId) {
        await updateStylist(selectedId, payload)
      }
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      const message =
        getFieldError(err, 'name') ??
        getFieldError(err, 'wps_employee_id') ??
        getFieldError(err, 'bank_iban') ??
        getFieldError(err, 'commission_profile_id') ??
        err?.response?.data?.message ??
        'Unable to save stylist.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <UserRound className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Stylists</h1>
            <p className="text-sm text-muted-foreground">
              Staff profiles, commission profiles, and UAE WPS payroll fields.
            </p>
          </div>
        </div>
        {activeTab === 'staff' ? (
          <Button type="button" variant="outline" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New stylist
          </Button>
        ) : null}
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActiveTab('staff')}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'staff'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/70 hover:bg-muted',
          )}
        >
          <UserRound className="h-4 w-4" />
          Staff
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('profiles')}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'profiles'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/70 hover:bg-muted',
          )}
        >
          <Percent className="h-4 w-4" />
          Commission profiles
        </button>
      </div>

      {activeTab === 'profiles' ? <CommissionProfilesPanel /> : null}

      {activeTab === 'staff' ? (
        <>
          <MasterServerTable
            key={activeBranchId}
            fetchPage={fetchPage}
            reloadKey={reloadKey}
            getRowKey={(stylist) => stylist.id}
            onEdit={openEdit}
            emptyLabel="No stylists yet."
            searchPlaceholder="Search stylists…"
            exportColumns={EXPORT_COLUMNS}
            exportFilename="stylists.csv"
            fetchAllForExport={(params) => fetchStylistsList(params)}
            columns={[
              {
                key: 'name',
                label: 'Name',
                sortable: true,
                render: (row) => (
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: row.colour ?? '#D4A373' }}
                      aria-hidden
                    />
                    {row.name}
                  </span>
                ),
              },
              {
                key: 'skills',
                label: 'Skills',
                render: (row) => (row.skills ?? []).length,
              },
              {
                key: 'commission_profile',
                label: 'Commission profile',
                render: (row) => row.commission_profile?.name ?? '—',
              },
              {
                key: 'bookable_online',
                label: 'Online booking',
                render: (row) => (row.bookable_online ? 'Yes' : 'No'),
              },
              {
                key: 'is_active',
                label: 'Status',
                render: (row) => (row.is_active ? 'Active' : 'Inactive'),
              },
            ]}
          />

          <MasterFormModal
            open={modalOpen}
            onClose={closeModal}
            title={creating ? 'New stylist' : 'Edit stylist'}
            description="Staff profile, skills, calendar colour, and UAE WPS payroll fields."
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_140px]">
                <div>
                  <label htmlFor="stylist-name" className="mb-1.5 block text-sm font-medium">
                    Name
                  </label>
                  <input
                    id="stylist-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    className={cn(
                      'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                      'focus:outline-none focus:ring-2 focus:ring-ring',
                    )}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="stylist-colour" className="mb-1.5 block text-sm font-medium">
                    Calendar colour
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="stylist-colour"
                      type="color"
                      value={form.colour}
                      onChange={(e) => updateField('colour', e.target.value)}
                      className="h-11 w-14 cursor-pointer rounded-xl border border-border/80 bg-card p-1"
                    />
                    <input
                      type="text"
                      value={form.colour}
                      onChange={(e) => updateField('colour', e.target.value)}
                      className={cn(
                        'h-11 min-w-0 flex-1 rounded-xl border border-border/80 bg-card px-4 text-sm',
                        'focus:outline-none focus:ring-2 focus:ring-ring',
                      )}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="stylist-skills" className="mb-1.5 block text-sm font-medium">
                  Skills
                </label>
                <div className="mb-2 flex flex-wrap gap-2">
                  {form.skills.map((skill) => (
                    <span
                      key={skill}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium"
                    >
                      {formatSkillLabel(skill)}
                      <button
                        type="button"
                        onClick={() => removeSkill(skill)}
                        className="rounded-full p-0.5 hover:bg-background"
                        aria-label={`Remove ${formatSkillLabel(skill)}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  id="stylist-skills"
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={handleSkillKeyDown}
                  placeholder="Type a skill and press Enter"
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {COMMON_STYLIST_SKILLS.map((skill) => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => addSkill(skill)}
                      className="rounded-full border border-border/80 px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-muted/80"
                    >
                      + {formatSkillLabel(skill)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="stylist-wps" className="mb-1.5 block text-sm font-medium">
                    WPS employee ID
                  </label>
                  <input
                    id="stylist-wps"
                    type="text"
                    value={form.wps_employee_id}
                    onChange={(e) => updateField('wps_employee_id', e.target.value)}
                    className={cn(
                      'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                      'focus:outline-none focus:ring-2 focus:ring-ring',
                    )}
                    placeholder="MOHRE / WPS reference"
                  />
                </div>
                <div>
                  <label htmlFor="stylist-iban" className="mb-1.5 block text-sm font-medium">
                    Bank IBAN
                  </label>
                  <input
                    id="stylist-iban"
                    type="text"
                    value={form.bank_iban}
                    onChange={(e) => updateField('bank_iban', e.target.value)}
                    className={cn(
                      'h-11 w-full rounded-xl border border-border/80 bg-card px-4 font-mono text-sm uppercase',
                      'focus:outline-none focus:ring-2 focus:ring-ring',
                    )}
                    placeholder="AE00 0000 0000 0000 0000 000"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="stylist-commission-profile"
                  className="mb-1.5 block text-sm font-medium"
                >
                  Commission profile
                </label>
                <select
                  id="stylist-commission-profile"
                  value={form.commission_profile_id}
                  onChange={(e) => updateField('commission_profile_id', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                >
                  <option value="">No commission profile</option>
                  {commissionProfiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                      {profile.summary ? ` — ${profile.summary}` : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  Manage rates on the Commission profiles tab. Commission accrues when a stylist is
                  assigned on a paid sale line.
                </p>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.bookable_online}
                  onChange={(e) => updateField('bookable_online', e.target.checked)}
                  className="rounded border-border"
                />
                Bookable online
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => updateField('is_active', e.target.checked)}
                  className="rounded border-border"
                />
                Stylist is active
              </label>

              {error ? (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Saving…' : creating ? 'Create stylist' : 'Save stylist'}
                </Button>
              </div>
            </form>
          </MasterFormModal>
        </>
      ) : null}
    </div>
  )
}
